import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiCheck, FiX, FiLoader, FiMapPin, FiUser, FiUsers, FiCheckCircle, FiStar } from 'react-icons/fi';
import { getRecommendedPlan } from '../config/features';
import api from '../api';

// 한국 행정구역 데이터
const REGIONS = {
  '서울특별시': ['강남구', '강동구', '강북구', '강서구', '관악구', '광진구', '구로구', '금천구', '노원구', '도봉구', '동대문구', '동작구', '마포구', '서대문구', '서초구', '성동구', '성북구', '송파구', '양천구', '영등포구', '용산구', '은평구', '종로구', '중구', '중랑구'],
  '부산광역시': ['강서구', '금정구', '기장군', '남구', '동구', '동래구', '부산진구', '북구', '사상구', '사하구', '서구', '수영구', '연제구', '영도구', '중구', '해운대구'],
  '대구광역시': ['남구', '달서구', '달성군', '동구', '북구', '서구', '수성구', '중구'],
  '인천광역시': ['강화군', '계양구', '남동구', '동구', '미추홀구', '부평구', '서구', '연수구', '옹진군', '중구'],
  '광주광역시': ['광산구', '남구', '동구', '북구', '서구'],
  '대전광역시': ['대덕구', '동구', '서구', '유성구', '중구'],
  '울산광역시': ['남구', '동구', '북구', '울주군', '중구'],
  '세종특별자치시': ['세종시'],
  '경기도': ['가평군', '고양시', '과천시', '광명시', '광주시', '구리시', '군포시', '김포시', '남양주시', '동두천시', '부천시', '성남시', '수원시', '시흥시', '안산시', '안성시', '안양시', '양주시', '양평군', '여주시', '연천군', '오산시', '용인시', '의왕시', '의정부시', '이천시', '파주시', '평택시', '포천시', '하남시', '화성시'],
  '강원특별자치도': ['강릉시', '고성군', '동해시', '삼척시', '속초시', '양구군', '양양군', '영월군', '원주시', '인제군', '정선군', '철원군', '춘천시', '태백시', '평창군', '홍천군', '화천군', '횡성군'],
  '충청북도': ['괴산군', '단양군', '보은군', '영동군', '옥천군', '음성군', '제천시', '증평군', '진천군', '청주시', '충주시'],
  '충청남도': ['계룡시', '공주시', '금산군', '논산시', '당진시', '보령시', '부여군', '서산시', '서천군', '아산시', '예산군', '천안시', '청양군', '태안군', '홍성군'],
  '전북특별자치도': ['고창군', '군산시', '김제시', '남원시', '무주군', '부안군', '순창군', '완주군', '익산시', '임실군', '장수군', '전주시', '정읍시', '진안군'],
  '전라남도': ['강진군', '고흥군', '곡성군', '광양시', '구례군', '나주시', '담양군', '목포시', '무안군', '보성군', '순천시', '신안군', '여수시', '영광군', '영암군', '완도군', '장성군', '장흥군', '진도군', '함평군', '해남군', '화순군'],
  '경상북도': ['경산시', '경주시', '고령군', '구미시', '군위군', '김천시', '문경시', '봉화군', '상주시', '성주군', '안동시', '영덕군', '영양군', '영주시', '영천시', '예천군', '울릉군', '울진군', '의성군', '청도군', '청송군', '칠곡군', '포항시'],
  '경상남도': ['거제시', '거창군', '고성군', '김해시', '남해군', '밀양시', '사천시', '산청군', '양산시', '의령군', '진주시', '창녕군', '창원시', '통영시', '하동군', '함안군', '함양군', '합천군'],
  '제주특별자치도': ['서귀포시', '제주시']
};

// 동/읍/면 데이터 (주요 지역)
const DISTRICTS = {
  '서울특별시': {
    '강남구': ['개포동', '논현동', '대치동', '도곡동', '삼성동', '세곡동', '수서동', '신사동', '압구정동', '역삼동', '율현동', '일원동', '자곡동', '청담동'],
    '강동구': ['강일동', '고덕동', '길동', '둔촌동', '명일동', '상일동', '성내동', '암사동', '천호동'],
    '강북구': ['미아동', '번동', '수유동', '우이동'],
    '강서구': ['가양동', '개화동', '공항동', '과해동', '내발산동', '등촌동', '마곡동', '방화동', '염창동', '오곡동', '오쇠동', '외발산동', '화곡동'],
    '관악구': ['낙성대동', '남현동', '봉천동', '신림동'],
    '광진구': ['광장동', '구의동', '군자동', '능동', '자양동', '중곡동', '화양동'],
    '구로구': ['가리봉동', '고척동', '구로동', '궁동', '개봉동', '오류동', '온수동', '천왕동', '항동', '신도림동'],
    '금천구': ['가산동', '독산동', '시흥동'],
    '노원구': ['공릉동', '상계동', '월계동', '중계동', '하계동'],
    '도봉구': ['도봉동', '방학동', '쌍문동', '창동'],
    '동대문구': ['답십리동', '신설동', '용두동', '이문동', '장안동', '전농동', '제기동', '청량리동', '회기동', '휘경동'],
    '동작구': ['노량진동', '대방동', '동작동', '본동', '사당동', '상도동', '신대방동', '흑석동'],
    '마포구': ['공덕동', '노고산동', '당인동', '대흥동', '도화동', '동교동', '마포동', '망원동', '상수동', '상암동', '서교동', '성산동', '신공덕동', '신수동', '신정동', '아현동', '연남동', '염리동', '용강동', '중동', '창전동', '토정동', '합정동', '현석동'],
    '서대문구': ['남가좌동', '냉천동', '대신동', '대현동', '미근동', '봉원동', '북가좌동', '북아현동', '신촌동', '연희동', '영천동', '옥천동', '창천동', '천연동', '충정로', '합동', '현저동', '홍은동', '홍제동'],
    '서초구': ['내곡동', '반포동', '방배동', '서초동', '신원동', '양재동', '염곡동', '우면동', '원지동', '잠원동'],
    '성동구': ['금호동', '도선동', '마장동', '사근동', '상왕십리동', '성수동', '송정동', '옥수동', '용답동', '응봉동', '하왕십리동', '행당동', '홍익동'],
    '성북구': ['길음동', '돈암동', '동선동', '동소문동', '보문동', '삼선동', '상월곡동', '석관동', '성북동', '안암동', '월곡동', '장위동', '정릉동', '종암동', '하월곡동'],
    '송파구': ['가락동', '거여동', '마천동', '문정동', '방이동', '삼전동', '석촌동', '송파동', '신천동', '오금동', '잠실동', '장지동', '풍납동'],
    '양천구': ['목동', '신월동', '신정동'],
    '영등포구': ['당산동', '대림동', '도림동', '문래동', '신길동', '양평동', '여의도동', '영등포동'],
    '용산구': ['갈월동', '남영동', '도원동', '동빙고동', '동자동', '문배동', '보광동', '산천동', '서계동', '서빙고동', '신계동', '신창동', '용문동', '용산동', '원효로', '이촌동', '이태원동', '주성동', '청암동', '청파동', '한강로', '한남동', '효창동', '후암동'],
    '은평구': ['갈현동', '구산동', '녹번동', '대조동', '불광동', '수색동', '신사동', '역촌동', '응암동', '증산동', '진관동'],
    '종로구': ['가회동', '견지동', '경운동', '계동', '공평동', '관철동', '관훈동', '교남동', '교북동', '구기동', '궁정동', '권농동', '낙원동', '내수동', '내자동', '누상동', '누하동', '당주동', '도렴동', '돈의동', '동숭동', '명륜동', '묘동', '무악동', '봉익동', '부암동', '사간동', '사직동', '삼청동', '서린동', '세종로', '송월동', '송현동', '수송동', '숭인동', '신교동', '신문로', '신영동', '안국동', '연건동', '연지동', '예지동', '옥인동', '운니동', '원남동', '원서동', '이화동', '익선동', '인사동', '인의동', '장사동', '재동', '적선동', '중학동', '창성동', '창신동', '청운동', '청진동', '체부동', '충신동', '통의동', '통인동', '팔판동', '평동', '평창동', '필운동', '행촌동', '혜화동', '홍지동', '홍파동', '화동', '효자동', '효제동', '훈정동'],
    '중구': ['광희동', '남대문로', '남산동', '남창동', '남학동', '다동', '만리동', '명동', '무교동', '무학동', '묵정동', '방산동', '봉래동', '북창동', '산림동', '삼각동', '서소문동', '소공동', '수표동', '수하동', '순화동', '신당동', '쌍림동', '예관동', '예장동', '오장동', '을지로', '의주로', '인현동', '입정동', '장교동', '장충동', '저동', '정동', '주교동', '주자동', '중림동', '초동', '충무로', '충정로', '필동', '황학동', '회현동', '흥인동'],
    '중랑구': ['망우동', '면목동', '묵동', '상봉동', '신내동', '중화동']
  },
  '경기도': {
    '성남시': {
      '분당구': ['분당동', '수내동', '정자동', '서현동', '야탑동', '이매동', '판교동', '삼평동', '백현동', '운중동', '금곡동', '구미동', '대장동'],
      '수정구': ['신흥동', '수진동', '태평동', '단대동', '복정동', '산성동', '양지동', '신촌동', '고등동'],
      '중원구': ['성남동', '중앙동', '금광동', '은행동', '상대원동', '하대원동', '도촌동']
    },
    '수원시': {
      '장안구': ['파장동', '율전동', '정자동', '영화동', '송죽동', '조원동', '연무동'],
      '권선구': ['권선동', '세류동', '평동', '서둔동', '구운동', '금곡동', '호매실동', '곡선동', '대황교동'],
      '팔달구': ['매교동', '매산동', '고등동', '화서동', '지동', '우만동', '인계동', '남창동', '북수동'],
      '영통구': ['매탄동', '원천동', '영통동', '망포동', '이의동', '하동', '광교동']
    },
    '용인시': {
      '수지구': ['죽전동', '신봉동', '동천동', '성복동', '상현동', '풍덕천동'],
      '기흥구': ['마북동', '동백동', '중동', '상갈동', '하갈동', '보라동', '지곡동', '공세동', '구갈동', '신갈동', '영덕동'],
      '처인구': ['포곡읍', '모현읍', '이동읍', '남사읍', '원삼면', '백암면', '양지면', '김량장동', '삼가동', '역북동', '마평동', '운학동']
    },
    '고양시': {
      '일산동구': ['장항동', '식사동', '중산동', '정발산동', '풍산동', '백석동', '마두동'],
      '일산서구': ['주엽동', '대화동', '탄현동', '가좌동', '일산동'],
      '덕양구': ['토당동', '주교동', '행신동', '능곡동', '화정동', '행주동', '덕양동', '고양동', '신원동', '용두동', '효자동', '원당동', '원흥동', '삼송동', '지축동', '창릉동']
    },
    '부천시': ['원미동', '소사동', '역곡동', '춘의동', '중동', '상동', '심곡동', '송내동', '도당동', '약대동', '범박동', '오정동'],
    '안양시': {
      '만안구': ['안양동', '석수동'],
      '동안구': ['비산동', '평촌동', '관양동', '호계동', '범계동']
    },
    '안산시': {
      '상록구': ['본오동', '사동', '부곡동', '월피동', '성포동', '반월동'],
      '단원구': ['고잔동', '원곡동', '초지동', '선부동', '대부동', '화정동']
    },
    '평택시': ['비전동', '서정동', '지산동', '통복동', '세교동', '안중읍', '포승읍', '진위면', '청북읍', '송탄동', '신장동', '고덕동'],
    '화성시': ['동탄동', '병점동', '봉담읍', '향남읍', '남양읍', '우정읍', '장안면', '매송면', '송산면', '서신면', '팔탄면', '정남면', '마도면', '양감면', '비봉면'],
    '남양주시': ['호평동', '평내동', '금곡동', '일패동', '이패동', '삼패동', '지금동', '도농동', '진접읍', '화도읍', '와부읍', '조안면', '별내동'],
    '시흥시': ['대야동', '정왕동', '은행동', '신천동', '신현동', '매화동', '능곡동', '월곶동', '군자동'],
    '광명시': ['광명동', '철산동', '하안동', '소하동'],
    '군포시': ['산본동', '금정동', '당정동', '당동', '도마교동', '둔대동'],
    '의왕시': ['고천동', '내손동', '오전동', '청계동', '삼동'],
    '하남시': ['덕풍동', '신장동', '풍산동', '창우동', '초이동', '망월동', '감일동', '감북동', '춘궁동'],
    '오산시': ['오산동', '원동', '부산동', '세마동', '가장동', '수청동', '금암동', '내삼미동', '외삼미동', '갈곶동', '양산동'],
    '이천시': ['창전동', '중리동', '관고동', '증포동', '갈산동', '장호원읍', '부발읍', '신둔면', '백사면', '율면', '대월면', '호법면', '모가면'],
    '안성시': ['안성동', '공도읍', '보개면', '금광면', '서운면', '미양면', '대덕면', '양성면', '원곡면', '일죽면', '죽산면', '삼죽면'],
    '김포시': ['김포동', '사우동', '풍무동', '장기동', '고촌읍', '통진읍', '월곶면', '하성면', '대곶면', '양촌읍'],
    '광주시': ['경안동', '송정동', '오포읍', '초월읍', '곤지암읍', '도척면', '퇴촌면', '남종면', '남한산성면'],
    '양주시': ['회천동', '양주동', '백석읍', '은현면', '남면', '광적면', '장흥면'],
    '포천시': ['포천동', '군내면', '소흘읍', '신북면', '가산면', '영중면', '일동면', '이동면', '영북면', '관인면', '창수면', '내촌면', '화현면'],
    '여주시': ['여주동', '점동면', '능서면', '흥천면', '금사면', '산북면', '대신면', '북내면', '강천면'],
    '파주시': ['금촌동', '운정동', '교하동', '문산읍', '조리읍', '파주읍', '월롱면', '탄현면', '법원읍', '광탄면', '파평면', '적성면', '장단면', '진동면', '진서면', '군내면'],
    '양평군': ['양평읍', '강상면', '강하면', '양서면', '옥천면', '서종면', '단월면', '청운면', '양동면', '지평면', '용문면', '개군면'],
    '동두천시': ['생연동', '중앙동', '보산동', '송내동', '안흥동', '상패동', '하봉암동'],
    '과천시': ['중앙동', '갈현동', '별양동', '부림동', '주암동'],
    '구리시': ['갈매동', '인창동', '교문동', '수택동', '아천동'],
    '연천군': ['연천읍', '전곡읍', '청산면', '백학면', '미산면', '왕징면', '신서면', '중면', '장남면', '군남면'],
    '가평군': ['가평읍', '설악면', '청평면', '상면', '조종면', '북면']
  },
  '부산광역시': {
    '해운대구': ['우동', '중동', '좌동', '송정동', '재송동', '반여동', '반송동', '석대동'],
    '부산진구': ['부전동', '전포동', '당감동', '범전동', '범천동', '연지동', '초읍동', '양정동', '가야동', '개금동'],
    '남구': ['대연동', '용호동', '용당동', '감만동', '우암동', '문현동'],
    '동래구': ['명륜동', '온천동', '사직동', '복천동', '수안동', '명장동', '안락동'],
    '강서구': ['강동동', '명지동', '대저동', '녹산동', '신호동', '가락동', '천가동'],
    '금정구': ['금사동', '부곡동', '장전동', '선동', '청룡동', '남산동', '구서동', '금성동', '서동', '회동'],
    '기장군': ['기장읍', '장안읍', '정관읍', '일광면', '철마면'],
    '동구': ['초량동', '수정동', '좌천동', '범일동', '범천동', '진교동', '불로동'],
    '북구': ['구포동', '금곡동', '화명동', '덕천동', '만덕동'],
    '사상구': ['삼락동', '모라동', '덕포동', '괘법동', '감전동', '주례동', '학장동', '엄궁동'],
    '사하구': ['괴정동', '당리동', '하단동', '신평동', '장림동', '다대동', '구평동', '감천동'],
    '서구': ['동대신동', '서대신동', '부민동', '아미동', '초장동', '충무동', '남부민동', '암남동'],
    '수영구': ['남천동', '광안동', '수영동', '망미동', '민락동'],
    '연제구': ['거제동', '연산동'],
    '영도구': ['남항동', '영선동', '신선동', '봉래동', '청학동', '동삼동'],
    '중구': ['중앙동', '동광동', '대청동', '보수동', '부평동', '광복동', '남포동', '영주동']
  },
  '대구광역시': {
    '남구': ['대명동', '봉덕동', '이천동'],
    '달서구': ['성당동', '두류동', '본동', '감삼동', '죽전동', '장기동', '용산동', '이곡동', '신당동', '월성동', '진천동', '상인동', '도원동'],
    '달성군': ['화원읍', '논공읍', '다사읍', '유가읍', '옥포읍', '현풍읍', '가창면', '하빈면', '구지면'],
    '동구': ['신암동', '신천동', '효목동', '도평동', '불로동', '지묘동', '동촌동', '방촌동', '해안동', '안심동'],
    '북구': ['산격동', '검단동', '칠성동', '침산동', '구암동', '읍내동', '복현동', '대현동', '노원동', '관음동', '팔달동', '국우동'],
    '서구': ['내당동', '비산동', '평리동', '상리동', '원대동', '이현동', '중리동'],
    '수성구': ['범어동', '만촌동', '수성동', '황금동', '중동', '상동', '파동', '두산동', '지산동', '매호동', '사월동', '대흥동'],
    '중구': ['동인동', '삼덕동', '성내동', '대신동', '남산동', '대봉동', '포정동']
  },
  '인천광역시': {
    '강화군': ['강화읍', '선원면', '불은면', '길상면', '화도면', '양도면', '내가면', '하점면', '양사면', '송해면', '교동면', '삼산면'],
    '계양구': ['계산동', '작전동', '효성동', '계양동', '병방동', '용종동', '임학동', '박촌동', '동양동', '귀빈동', '상야동', '하야동', '평동', '노오지동', '선주지동', '이화동', '오류동', '갈현동'],
    '남동구': ['간석동', '구월동', '만수동', '장수동', '서창동', '운연동', '논현동'],
    '동구': ['만석동', '화수동', '송현동', '화평동', '창영동', '송림동'],
    '미추홀구': ['숭의동', '용현동', '학익동', '도화동', '주안동', '관교동', '문학동'],
    '부평구': ['부평동', '산곡동', '청천동', '갈산동', '삼산동', '부개동', '일신동', '십정동'],
    '서구': ['검단동', '가정동', '석남동', '연희동', '심곡동', '가좌동', '신현동', '원창동', '당하동'],
    '연수구': ['연수동', '청학동', '동춘동', '송도동', '옥련동'],
    '옹진군': ['북도면', '백령면', '대청면', '덕적면', '자월면', '영흥면', '연평면'],
    '중구': ['중앙동', '해안동', '항동', '관동', '송학동', '송월동', '신포동', '선화동', '답동', '연안동', '유동', '경동', '북성동', '용동', '전동', '운북동', '운남동', '도원동', '율목동', '동인천동', '인현동', '신흥동', '신생동', '선린동', '남북동', '묘동', '항동', '사동', '금곡동']
  },
  '광주광역시': {
    '광산구': ['송정동', '도산동', '평동', '비아동', '우산동', '월곡동', '수완동', '임곡동', '신가동', '신창동', '운남동', '진곡동', '하남동', '삼도동'],
    '남구': ['월산동', '방림동', '봉선동', '사직동', '주월동', '효덕동', '승촌동', '대촌동', '칠석동', '압촌동', '양림동', '양과동', '백운동', '구동', '서동', '노대동'],
    '동구': ['동명동', '계림동', '산수동', '지산동', '충장동', '금남로', '대인동', '궁동', '학동', '소태동', '불로동', '선교동', '용연동', '장동', '남동', '학운동', '지원동', '월남동', '황금동'],
    '북구': ['중흥동', '유동', '누문동', '운암동', '신안동', '용봉동', '동림동', '오치동', '문흥동', '각화동', '두암동', '덕의동', '임동', '건국동', '삼각동', '매곡동', '망월동', '청풍동', '화암동', '운정동', '우산동', '풍향동', '신용동', '양산동', '본촌동', '일곡동', '연제동', '삼각동'],
    '서구': ['농성동', '양동', '유촌동', '덕흥동', '쌍촌동', '화정동', '벽진동', '금호동', '풍암동', '서창동', '용두동', '치평동', '동천동', '광천동', '내방동', '마륵동', '매월동']
  },
  '대전광역시': {
    '대덕구': ['오정동', '회덕동', '중리동', '법동', '신탄진동', '석봉동', '덕암동', '목상동', '비래동', '와동', '송촌동', '문평동'],
    '동구': ['중앙동', '신인동', '판암동', '용운동', '대동', '자양동', '가양동', '효동', '삼성동', '홍도동', '성남동', '사성동', '가오동', '세천동', '소제동', '인동', '효평동', '신흥동', '대청동', '추동', '정동', '용전동', '직동', '산내동', '천동', '신상동', '비룡동', '마산동', '덕진동', '구도동'],
    '서구': ['변동', '용문동', '탄방동', '둔산동', '월평동', '갈마동', '가수원동', '관저동', '괴정동', '도마동', '정림동', '복수동', '가장동', '내동', '만년동', '흑석동'],
    '유성구': ['진잠동', '온천동', '노은동', '신성동', '전민동', '구즉동', '관평동', '대정동', '원신흥동', '봉명동', '장대동', '궁동', '도룡동', '문지동'],
    '중구': ['은행동', '선화동', '대흥동', '문창동', '석교동', '대사동', '부사동', '용두동', '오류동', '태평동', '유천동', '문화동', '산성동', '안영동', '목동', '중촌동', '대흥동', '무수동', '구완동', '호동']
  },
  '울산광역시': {
    '남구': ['삼산동', '신정동', '달동', '무거동', '옥동', '야음동', '여천동', '선암동', '두왕동', '고사동', '용연동', '용잠동', '상개동', '황성동'],
    '동구': ['방어동', '일산동', '화정동', '대송동', '전하동', '주전동', '서부동', '남목동', '미포동'],
    '북구': ['농소동', '연암동', '송정동', '양정동', '효문동', '중산동', '진장동', '달천동', '천곡동', '산하동', '명촌동', '정자동', '매곡동', '구유동', '호계동', '이화동', '신명동', '어물동', '무룡동'],
    '울주군': ['온산읍', '온양읍', '언양읍', '서생면', '웅촌면', '범서읍', '청량면', '상북면', '삼남면', '삼동면', '두동면', '두서면'],
    '중구': ['성남동', '복산동', '옥교동', '다운동', '우정동', '학성동', '반구동', '성안동', '교동', '병영동', '태화동']
  },
  '세종특별자치시': {
    '세종시': ['조치원읍', '연기면', '연동면', '부강면', '금남면', '장군면', '연서면', '전의면', '전동면', '소정면', '한솔동', '도담동', '아름동', '종촌동', '고운동', '보람동', '대평동', '새롬동', '나성동', '다정동', '어진동', '해밀동', '소담동', '반곡동', '합강동', '온누리동']
  },
  '강원특별자치도': {
    '춘천시': ['중앙로', '조양동', '죽림동', '온의동', '약사명동', '칠전동', '효자동', '석사동', '퇴계동', '근화동', '소양동', '후평동', '우두동', '신동', '동면', '신북읍', '동산면', '서면', '남면', '남산면', '북산면', '사북면'],
    '원주시': ['중앙동', '원인동', '인동', '개운동', '명륜동', '태장동', '단구동', '단계동', '무실동', '학성동', '행구동', '봉산동', '반곡동', '문막읍', '소초면', '호저면', '지정면', '부론면', '귀래면', '흥업면', '판부면', '신림면'],
    '강릉시': ['홍제동', '중앙동', '옥천동', '교동', '금학동', '내곡동', '성덕동', '죽헌동', '담산동', '포남동', '임당동', '강남동', '성남동', '송정동', '노암동', '청량동', '유천동', '난곡동', '견소동', '입암동', '주문진읍', '성산면', '왕산면', '구정면', '강동면', '옥계면', '사천면', '연곡면'],
    '동해시': ['천곡동', '송정동', '북평동', '묵호동', '효가동', '삼화동', '지흥동', '만우동', '북삼동', '괴란동'],
    '태백시': ['황지동', '황연동', '문곡동', '장성동', '철암동', '소도동', '창죽동', '통동', '화전동', '혈동', '적각동'],
    '속초시': ['교동', '금호동', '노학동', '대포동', '도문동', '동명동', '설악동', '영랑동', '장사동', '조양동', '중앙동', '청학동'],
    '삼척시': ['교동', '남양동', '당저동', '도계읍', '원덕읍', '근덕면', '노곡면', '미로면', '가곡면', '신기면', '하장면'],
    '고성군': ['간성읍', '거진읍', '현내면', '죽왕면', '토성면', '수동면'],
    '양구군': ['양구읍', '국토정중앙면', '동면', '남면', '방산면', '해안면'],
    '양양군': ['양양읍', '서면', '손양면', '현북면', '현남면', '강현면'],
    '영월군': ['영월읍', '상동읍', '북면', '남면', '김삿갓면', '수주면', '주천면', '한반도면', '무릉도원면'],
    '인제군': ['인제읍', '북면', '남면', '기린면', '서화면', '상남면'],
    '정선군': ['정선읍', '고한읍', '사북읍', '신동읍', '여량면', '화암면', '남면', '북평면', '임계면'],
    '철원군': ['갈말읍', '동송읍', '김화읍', '철원읍', '서면', '근남면', '근북면', '원동면', '원남면', '임남면'],
    '평창군': ['평창읍', '미탄면', '방림면', '대화면', '봉평면', '용평면', '진부면', '대관령면'],
    '홍천군': ['홍천읍', '화촌면', '두촌면', '내촌면', '서석면', '동면', '남면', '서면', '북방면', '내면'],
    '화천군': ['화천읍', '간동면', '하남면', '상서면', '사내면'],
    '횡성군': ['횡성읍', '우천면', '안흥면', '둔내면', '갑천면', '청일면', '공근면', '서원면', '강림면']
  },
  '충청북도': {
    '청주시': {
      '상당구': ['중앙동', '성안동', '북문로', '서문동', '대성동', '영동', '금천동', '용암동', '용정동', '탑대성동', '영운동', '남문로', '남주동', '문화동', '서운동', '수동', '산성동', '낭성면', '미원면', '가덕면'],
      '서원구': ['사직동', '사창동', '수곡동', '성화동', '개신동', '죽림동', '장성동', '모충동', '분평동', '산남동', '현도면'],
      '흥덕구': ['봉명동', '송정동', '신봉동', '복대동', '가경동', '강서동', '운천동', '비하동', '신전동', '외북동', '오송읍', '강내면', '옥산면'],
      '청원구': ['우암동', '내덕동', '율량동', '사천동', '북이면', '오창읍', '내수읍', '강외면', '미호천동']
    },
    '충주시': ['성내동', '교현동', '칠금동', '지현동', '문화동', '호암동', '연수동', '용산동', '목행동', '금릉동', '달천동', '종민동', '안림동', '직동', '목벌동', '용탄동', '주덕읍', '살미면', '수안보면', '대소원면', '신니면', '노은면', '앙성면', '중앙탑면', '금가면', '동량면', '산척면', '엄정면'],
    '제천시': ['중앙로', '영서동', '의림동', '신월동', '천남동', '화산동', '청전동', '고암동', '하소동', '명동', '왕암동', '신백동', '장락동', '두학동', '남천동', '강제동', '송학면', '덕산면', '봉양읍', '금성면', '청풍면', '수산면', '백운면'],
    '괴산군': ['괴산읍', '청천면', '청안면', '소수면', '칠성면', '사리면', '문광면', '장연면', '연풍면', '도안면', '불정면'],
    '단양군': ['단양읍', '매포읍', '대강면', '가곡면', '영춘면', '어상천면', '적성면', '단성면'],
    '보은군': ['보은읍', '속리산면', '장안면', '마로면', '탄부면', '삼승면', '수한면', '회남면', '회인면', '내북면'],
    '영동군': ['영동읍', '황간면', '추풍령면', '매곡면', '상촌면', '양강면', '용산면', '용화면', '학산면', '양산면', '심천면'],
    '옥천군': ['옥천읍', '동이면', '안남면', '안내면', '청성면', '청산면', '이원면', '군서면', '군북면'],
    '음성군': ['음성읍', '금왕읍', '소이면', '원남면', '맹동면', '대소면', '삼성면', '생극면', '감곡면'],
    '증평군': ['증평읍', '도안면'],
    '진천군': ['진천읍', '덕산읍', '초평면', '문백면', '백곡면', '이월면']
  },
  '충청남도': {
    '천안시': {
      '동남구': ['봉명동', '신부동', '안서동', '원성동', '청당동', '구성동', '신방동', '사직동', '오룡동', '청수동', '쌍용동', '목천읍', '북면', '성남면', '수신면', '풍세면', '광덕면', '병천면'],
      '서북구': ['성정동', '불당동', '두정동', '백석동', '성성동', '차암동', '성거읍', '직산읍', '입장면', '성환읍']
    },
    '공주시': ['중학동', '금성동', '신관동', '웅진동', '금흥동', '장기면', '반포면', '의당면', '정안면', '우성면', '사곡면', '탄천면', '계룡면', '신풍면', '유구읍', '이인면'],
    '보령시': ['동대동', '서린동', '궁촌동', '명천동', '죽정동', '대천동', '신흑동', '신평동', '오천면', '천북면', '청소면', '청라면', '남포면', '주포면', '주산면', '미산면', '웅천읍'],
    '아산시': ['온천동', '권곡동', '모종동', '배방읍', '탕정면', '음봉면', '둔포면', '영인면', '인주면', '선장면', '도고면', '신창면', '송악면', '염치읍'],
    '서산시': ['동문동', '수석동', '갈산동', '온석동', '석남동', '예천동', '잠홍동', '죽성동', '양대동', '대산읍', '인지면', '부석면', '팔봉면', '지곡면', '성연면', '음암면', '운산면', '해미면', '고북면'],
    '논산시': ['취암동', '강경읍', '연무읍', '성동면', '광석면', '노성면', '상월면', '부적면', '연산면', '벌곡면', '양촌면', '가야곡면', '은진면', '채운면'],
    '계룡시': ['금암동', '두마면', '엄사면', '신도안면'],
    '당진시': ['읍내동', '우강면', '고대면', '석문면', '대호지면', '정미면', '면천면', '순성면', '당진동', '송악읍', '합덕읍', '신평면', '송산면'],
    '금산군': ['금산읍', '금성면', '제원면', '부리면', '군북면', '남일면', '남이면', '진산면', '복수면', '추부면'],
    '부여군': ['부여읍', '규암면', '은산면', '외산면', '내산면', '구룡면', '홍산면', '옥산면', '남면', '충화면', '양화면', '임천면', '장암면', '세도면', '석성면', '초촌면'],
    '서천군': ['서천읍', '장항읍', '마서면', '화양면', '기산면', '한산면', '마산면', '시초면', '문산면', '판교면', '종천면', '비인면'],
    '청양군': ['청양읍', '운곡면', '대치면', '정산면', '목면', '청남면', '장평면', '남양면', '화성면', '비봉면'],
    '홍성군': ['홍성읍', '광천읍', '홍북읍', '금마면', '홍동면', '장곡면', '은하면', '결성면', '서부면', '갈산면'],
    '예산군': ['예산읍', '삽교읍', '대술면', '신양면', '광시면', '대흥면', '응봉면', '덕산면', '봉산면', '고덕면', '신암면', '오가면'],
    '태안군': ['태안읍', '안면읍', '고남면', '남면', '근흥면', '소원면', '원북면', '이원면']
  },
  '전북특별자치도': {
    '전주시': {
      '완산구': ['중앙동', '풍남동', '노송동', '서신동', '삼천동', '효자동', '평화동', '중화산동', '색장동', '대성동', '태평동', '서서학동', '남노송동', '중노송동', '상림동', '효자동'],
      '덕진구': ['진북동', '덕진동', '금암동', '팔복동', '우아동', '인후동', '동산동', '여의동', '만성동', '호성동', '송천동']
    },
    '군산시': ['중앙로', '영화동', '월명동', '해망동', '삼학동', '신흥동', '금동', '개정동', '경암동', '금광동', '수송동', '미원동', '나운동', '조촌동', '나포면', '옥구읍', '옥산면', '회현면', '임피면', '서수면', '대야면', '개정면', '성산면', '옥서면'],
    '익산시': ['중앙동', '창인동', '남중동', '평화동', '인화동', '영등동', '모현동', '송학동', '마동', '어양동', '금마면', '왕궁면', '황등면', '함열읍', '오산면', '용안면', '성당면', '함라면', '웅포면', '여산면', '낭산면', '망성면', '춘포면'],
    '정읍시': ['수성동', '상동', '연지동', '농소동', '공평동', '금붕동', '흑암동', '시기동', '용계동', '북면', '이평면', '입암면', '신태인읍', '고부면', '영원면', '덕천면', '태인면', '정우면', '감곡면', '옵성면', '산내면', '산외면', '칠보면', '내장상동'],
    '남원시': ['동충동', '노암동', '향교동', '신정동', '왕정동', '조산동', '주생동', '금지면', '대강면', '사매면', '주천면', '수지면', '송동면', '운봉읍', '아영면', '산동면', '산내면', '덕과면', '보절면', '인월면'],
    '김제시': ['요촌동', '서암동', '신곡동', '백산면', '성덕면', '금구면', '공덕면', '죽산면', '만경읍', '부량면', '백구면', '광활면', '진봉면', '황산면', '봉남면', '월촌면', '용지면', '청하면', '월성동'],
    '완주군': ['삼례읍', '봉동읍', '용진읍', '상관면', '이서면', '소양면', '구이면', '고산면', '비봉면', '운주면', '화산면', '동상면', '경천면'],
    '진안군': ['진안읍', '용담면', '안천면', '동향면', '상전면', '백운면', '성수면', '마령면', '부귀면', '정천면', '주천면'],
    '무주군': ['무주읍', '무풍면', '설천면', '적상면', '안성면', '부남면'],
    '장수군': ['장수읍', '산서면', '번암면', '장계면', '천천면', '계남면', '계북면'],
    '임실군': ['임실읍', '청웅면', '운암면', '신평면', '신덕면', '성수면', '오수면', '관촌면', '강진면', '덕치면', '삼계면', '지사면'],
    '순창군': ['순창읍', '인계면', '동계면', '풍산면', '금과면', '팔덕면', '쌍치면', '복흥면', '적성면', '유등면', '구림면'],
    '고창군': ['고창읍', '아산면', '무장면', '공음면', '상하면', '해리면', '성송면', '대산면', '심원면', '흥덕면', '성내면', '신림면', '부안면'],
    '부안군': ['부안읍', '주산면', '동진면', '행안면', '계화면', '보안면', '변산면', '진서면', '백산면', '상서면', '하서면', '줄포면', '위도면']
  },
  '전라남도': {
    '목포시': ['용해동', '연산동', '죽교동', '산정동', '온금동', '유달동', '죽동', '대성동', '달동', '삼학동', '호남동', '상동', '하당동'],
    '여수시': ['중앙동', '광무동', '충무동', '동산동', '서강동', '대교동', '소라면', '율촌면', '화양면', '남면', '화정면', '삼산면', '돌산읍', '월호동', '여천동', '묘도동', '문수동', '국동', '웅천동'],
    '순천시': ['중앙동', '장천동', '조례동', '연향동', '저전동', '동외동', '인제동', '삼산동', '매곡동', '풍덕동', '조곡동', '덕월동', '왕조동', '생목동', '해룡면', '서면', '승주읍', '황전면', '월등면', '송광면', '주암면', '낙안면', '별량면', '상사면', '외서면'],
    '나주시': ['송월동', '금성동', '중앙동', '과원동', '이창동', '대호동', '영산동', '노안면', '공산면', '동강면', '다시면', '문평면', '세지면', '왕곡면', '반남면', '봉황면', '다도면', '금천면', '산포면', '영강면', '영산포읍'],
    '광양시': ['중동', '태인동', '광영동', '황금동', '초남동', '골약동', '광양읍', '봉강면', '옥룡면', '진상면', '진월면', '다압면'],
    '담양군': ['담양읍', '봉산면', '고서면', '가사문학면', '창평면', '대덕면', '무정면', '금성면', '용면', '수북면', '월산면', '대전면'],
    '곡성군': ['곡성읍', '오곡면', '삼기면', '석곡면', '목사동면', '죽곡면', '고달면', '옥과면', '입면', '겉절면', '도림면'],
    '구례군': ['구례읍', '문척면', '간전면', '토지면', '마산면', '광의면', '용방면', '산동면'],
    '고흥군': ['고흥읍', '도양읍', '풍양면', '도덕면', '금산면', '도화면', '포두면', '봉래면', '점암면', '남양면', '동일면', '대서면', '두원면', '과역면', '동강면', '영남면'],
    '보성군': ['보성읍', '벌교읍', '노동면', '미력면', '겸백면', '율어면', '득량면', '조성면', '회천면', '문덕면', '웅치면'],
    '화순군': ['화순읍', '한천면', '춘양면', '청풍면', '이서면', '능주면', '도곡면', '도암면', '이양면', '북면', '동면', '남면'],
    '장흥군': ['장흥읍', '관산읍', '대덕읍', '용산면', '안양면', '장동면', '장평면', '유치면', '부산면', '회진면'],
    '강진군': ['강진읍', '군동면', '칠량면', '대구면', '도암면', '신전면', '성전면', '작천면', '병영면', '옴천면', '마량면'],
    '해남군': ['해남읍', '삼산면', '화산면', '현산면', '송지면', '북평면', '북일면', '옥천면', '계곡면', '마산면', '문내면', '화원면', '산이면', '황산면'],
    '영암군': ['영암읍', '삼호읍', '덕진면', '금정면', '신북면', '시종면', '도포면', '군서면', '서호면', '학산면', '미암면'],
    '무안군': ['무안읍', '일로읍', '삼향읍', '몽탄면', '청계면', '현경면', '망운면', '해제면', '운남면'],
    '함평군': ['함평읍', '손불면', '학교면', '신광면', '엄다면', '대동면', '나산면', '해보면', '월야면'],
    '영광군': ['영광읍', '백수읍', '홍농읍', '대마면', '묘량면', '불갑면', '군서면', '염산면', '법성면', '낙월면', '군동면'],
    '장성군': ['장성읍', '진원면', '남면', '동화면', '삼서면', '삼계면', '황룡면', '서삼면', '북일면', '북이면', '북하면'],
    '완도군': ['완도읍', '군외면', '신지면', '고금면', '약산면', '청산면', '소안면', '금일읍', '노화읍', '보길면', '생일면', '금당면'],
    '진도군': ['진도읍', '군내면', '고군면', '의신면', '임회면', '지산면', '조도면'],
    '신안군': ['지도읍', '압해읍', '증도면', '임자면', '자은면', '비금면', '도초면', '흑산면', '하의면', '신의면', '장산면', '안좌면', '팔금면']
  },
  '경상북도': {
    '포항시': {
      '남구': ['상도동', '해도동', '송도동', '청림동', '효자동', '대도동', '장흥동', '구룡포읍', '연일읍', '오천읍', '대송면', '동해면', '장기면', '호미곶면'],
      '북구': ['학산동', '죽도동', '창포동', '두호동', '환호동', '흥해읍', '신광면', '청하면', '송라면', '기계면', '죽장면', '기북면']
    },
    '경주시': ['황남동', '황오동', '성건동', '동천동', '인왕동', '탑동', '노동동', '노서동', '성동동', '광명동', '용강동', '보문동', '불국동', '진현동', '천북면', '양북면', '강동면', '내남면', '외동읍', '안강읍', '감포읍', '건천읍', '양남면', '서면'],
    '김천시': ['평화동', '삼락동', '대신동', '대곡동', '모암동', '황금동', '지좌동', '부곡동', '감천면', '어모면', '봉산면', '대항면', '남면', '개령면', '아포읍', '농소면', '남면', '구성면', '지례면', '부항면', '대덕면', '증산면'],
    '안동시': ['중구동', '명륜동', '북문동', '동부동', '남문동', '법상동', '남후면', '도산면', '녹전면', '북후면', '서후면', '송하면', '와룡면', '예안면', '임동면', '임하면', '일직면', 'ㅈ정하면', '풍산읍', '풍천면'],
    '구미시': ['송정동', '원평동', '지산동', '비산동', '신평동', '형곡동', '진미동', '광평동', '상모동', '공단동', '임수동', '인의동', '거의동', '양호동', '도량동', '선산읍', '고아읍', '무을면', '옥성면', '도개면', '해평면', '산동면', '장천면'],
    '영주시': ['영주동', '휴천동', '상망동', '하망동', '문정동', '조제동', '가흥동', '풍기읍', '이산면', '평은면', '문수면', '장수면', '안정면', '봉현면', '순흥면', '단산면', '부석면'],
    '영천시': ['완산동', '문내동', '문외동', '과전동', '언하동', '오수동', '쌍계동', '야사동', '교촌동', '금호읍', '청통면', '신녕면', '화산면', '화북면', '화남면', '북안면', '자양면', '임고면', '고경면', '대창면'],
    '상주시': ['남성동', '남장동', '남원동', '계림동', '서성동', '북문동', '냉림동', '중동', '초산동', '청리면', '공성면', '외서면', '외남면', '사벌국면', '중동면', '공검면', '낙동면', '이안면', '함창읍', '은척면', '화북면', '화동면', '화서면', '모동면', '모서면', '모동면', '화남면'],
    '문경시': ['점촌동', '흥덕동', '영순면', '산양면', '호계면', '가은읍', '농암면', '마성면', '동로면', '산북면'],
    '경산시': ['중방동', '사정동', '삼북동', '신천동', '중산동', '압량읍', '남산면', '남천면', '와촌면', '자인면', '용성면', '하양읍', '진량읍'],
    '군위군': ['군위읍', '소보면', '효령면', '부계면', '우보면', '의흥면', '산성면', '삼국유사면', '고로면'],
    '의성군': ['의성읍', '단촌면', '점곡면', '사곡면', '금성면', '봉양면', '옥산면', '다인면', '구천면', '안계면', '비안면', '안평면', '신평면', '춘산면', '가음면', '단밀면'],
    '청송군': ['청송읍', '부남면', '현동면', '현서면', '안덕면', '파천면', '진보면', '주왕산면'],
    '영양군': ['영양읍', '입암면', '청기면', '일월면', '수비면', '석보면'],
    '영덕군': ['영덕읍', '강구면', '남정면', '달산면', '지품면', '축산면', '영해면', '병곡면', '창수면'],
    '청도군': ['화양읍', '청도읍', '각남면', '각북면', '풍각면', '이서면', '운문면', '금천면', '매전면'],
    '고령군': ['고령읍', '쌍림면', '운수면', '개진면', '성산면', '다산면', '우곡면', '덕곡면'],
    '성주군': ['성주읍', '선남면', '용암면', '수륜면', '벽진면', '초전면', '금수면', '대가면', '가천면', '월항면'],
    '칠곡군': ['왜관읍', '북삼읍', '석적읍', '약목면', '기산면', '지천면', '동명면'],
    '예천군': ['예천읍', '용문면', '감천면', '보문면', '호명면', '유천면', '용궁면', '개포면', '지보면', '풍양면', '효자면', '은풍면'],
    '봉화군': ['봉화읍', '물야면', '봉성면', '법전면', '춘양면', '소천면', '석포면', '재산면', '명호면', '상운면'],
    '울진군': ['울진읍', '평해읍', '기성면', '온정면', '북면', '서면', '근남면', '죽변면', '후포면', '매화면'],
    '울릉군': ['울릉읍', '서면', '북면']
  },
  '경상남도': {
    '창원시': {
      '의창구': ['대산면', '동읍', '북면', '팔용동', '명곡동', '봉곡동', '용지동', '반계동', '사화동', '소답동', '퇴촌동'],
      '성산구': ['성주동', '상남동', '중앙동', '남양동', '사파동', '가음정동', '삼정자동', '귀산동', '웅남동', '완암동', '신촌동'],
      '마산합포구': ['중앙동', '오동동', '신포동', '월영동', '산호동', '진동면', '진북면', '진전면', '구산면'],
      '마산회원구': ['회원동', '양덕동', '합성동', '봉암동', '석전동', '교방동', '내서읍'],
      '진해구': ['이동', '태백동', '여좌동', '충무동', '경화동', '덕산동', '풍호동', '병암동', '남양동', '웅동', '웅천동', '용원동', '성내동']
    },
    '진주시': ['상봉동', '상대동', '하대동', '천전동', '성북동', '이현동', '평거동', '가좌동', '칠암동', '초장동', '집현동', '문산읍', '내동면', '정촌면', '금곡면', '진성면', '일반성면', '이반성면', '사봉면', '지수면', '대곡면', '금산면', '집산면', '미천면', '명석면', '대평면'],
    '통영시': ['중앙동', '도천동', '명정동', '태평동', '북신동', '무전동', '정량동', '도남동', '봉평동', '미수동', '광도면', '산양읍', '용남면', '도산면', '욕지면', '한산면', '사량면'],
    '사천시': ['동금동', '선구동', '벌용동', '향촌동', '대방동', '용현면', '축동면', '곤양면', '곤명면', '서포면', '정동면', '사남면'],
    '김해시': ['동상동', '회현동', '부원동', '내외동', '봉황동', '삼계동', '불암동', '율하동', '대동', '장유동', '진영읍', '주촌면', '진례면', '한림면', '생림면', '상동면'],
    '밀양시': ['내일동', '내이동', '교동', '삼문동', '가곡동', '활성동', '무안면', '상동면', '산외면', '산내면', '부북면', '상남면', '초동면', '단장면', '청도면'],
    '거제시': ['고현동', '장평동', '능포동', '아주동', '옥포동', '장승포동', '연초면', '하청면', '거제면', '동부면', '남부면', '사등면', '둔덕면', '일운면'],
    '양산시': ['중앙동', '강서동', '서창동', '소주동', '평산동', '덕계동', '명곡동', '물금읍', '동면', '원동면', '상북면', '하북면'],
    '의령군': ['의령읍', '가례면', '칠곡면', '대의면', '화정면', '용덕면', '정곡면', '지정면', '낙서면', '부림면', '봉수면', '궁류면', '유곡면'],
    '함안군': ['가야읍', '칠원읍', '함안면', '군북면', '법수면', '대산면', '칠서면', '칠북면', '산인면', '여항면'],
    '창녕군': ['창녕읍', '남지읍', '고암면', '성산면', '대합면', '이방면', '유어면', '대지면', '계성면', '영산면', '장마면', '도천면', '길곡면', '부곡면'],
    '고성군': ['고성읍', '삼산면', '하일면', '하이면', '상리면', '대가면', '영현면', '영오면', '개천면', '구만면', '회화면', '마암면', '동해면', '거류면'],
    '남해군': ['남해읍', '이동면', '상주면', '삼동면', '미조면', '남면', '서면', '고현면', '설천면', '창선면'],
    '하동군': ['하동읍', '화개면', '악양면', '적량면', '횡천면', '고전면', '금남면', '진교면', '양보면', '북천면', '청암면', '옥종면', '금성면'],
    '산청군': ['산청읍', '차황면', '오부면', '생초면', '금서면', '삼장면', '시천면', '단성면', '신안면', '생비량면', '신등면'],
    '함양군': ['함양읍', '마천면', '휴천면', '유림면', '수동면', '지곡면', '안의면', '서하면', '서상면', '백전면', '병곡면'],
    '거창군': ['거창읍', '주상면', '웅양면', '고제면', '북상면', '위천면', '마리면', '남상면', '남하면', '신원면', '가조면', '가북면'],
    '합천군': ['합천읍', '봉산면', '묘산면', '가야면', '야로면', '율곡면', '초계면', '쌍책면', '덕곡면', '청덕면', '적중면', '대양면', '쌍백면', '삼가면', '가회면', '대병면', '용주면']
  },
  '제주특별자치도': {
    '제주시': ['일도동', '이도동', '삼도동', '용담동', '건입동', '화북동', '삼양동', '봉개동', '아라동', '오라동', '연동', '노형동', '외도동', '이호동', '도두동', '한경면', '한림읍', '애월읍', '구좌읍', '조천읍', '우도면', '추자면'],
    '서귀포시': ['서귀동', '서홍동', '송산동', '정방동', '중앙동', '천지동', '효돈동', '영천동', '동홍동', '서호동', '호근동', '도순동', '회수동', '강정동', '법환동', '대륜동', '대포동', '중문동', '예래동', '하예동', '색달동', '상예동', '하원동', '보목동', '토평동', '남원읍', '성산읍', '안덕면', '대정읍', '표선면']
  }
};

export default function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [codeStatus, setCodeStatus] = useState({ checking: false, available: null, reason: '' });
  const [step, setStep] = useState(1); // 1: 기본정보, 2: 지역/운영체제, 3: 완료

  const [formData, setFormData] = useState({
    academyCode: '',
    academyName: '',
    adminEmail: '',
    adminPassword: '',
    adminPasswordConfirm: '',
    adminName: '',
    adminPhone: ''
  });

  // 지역 정보
  const [region, setRegion] = useState({
    province: '',
    city: '',
    district: '',
    subDistrict: ''
  });

  // 운영 체제
  const [operationType, setOperationType] = useState('');

  // 약관 동의
  const [agreements, setAgreements] = useState({
    termsOfService: false,
    privacyPolicy: false
  });

  // 시/군/구 옵션
  const cityOptions = region.province ? REGIONS[region.province] || [] : [];

  // 구 옵션 (시/군 선택 후)
  const districtData = region.province && region.city ? DISTRICTS[region.province]?.[region.city] : null;
  const hasDistricts = districtData && typeof districtData === 'object' && !Array.isArray(districtData);
  const districtOptions = hasDistricts ? Object.keys(districtData) : [];

  // 동/읍/면 옵션
  const subDistrictOptions = hasDistricts && region.district
    ? districtData[region.district] || []
    : (Array.isArray(districtData) ? districtData : []);

  // 학원코드 중복 체크 (디바운스)
  const checkAcademyCode = useCallback(async (code) => {
    if (!code || code.length < 3) {
      setCodeStatus({ checking: false, available: null, reason: '' });
      return;
    }

    setCodeStatus({ checking: true, available: null, reason: '' });

    try {
      const res = await api.get(`/api/auth/check-academy-code/${code}`);
      setCodeStatus({
        checking: false,
        available: res.data.available,
        reason: res.data.reason || ''
      });
    } catch (err) {
      setCodeStatus({ checking: false, available: null, reason: '' });
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkAcademyCode(formData.academyCode);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.academyCode, checkAcademyCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // 학원코드는 영문/숫자만 허용
    if (name === 'academyCode') {
      const cleaned = value.toLowerCase().replace(/[^a-z0-9]/g, '');
      setFormData({ ...formData, [name]: cleaned });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // 1단계 검증
  const validateStep1 = () => {
    if (formData.academyCode.length < 3) {
      setError('학원코드는 3자 이상이어야 합니다');
      return false;
    }

    if (!codeStatus.available) {
      setError('사용할 수 없는 학원코드입니다');
      return false;
    }

    if (!formData.academyName) {
      setError('학원명을 입력해주세요');
      return false;
    }

    if (!formData.adminName) {
      setError('이름을 입력해주세요');
      return false;
    }

    if (!formData.adminEmail) {
      setError('이메일을 입력해주세요');
      return false;
    }

    if (!formData.adminPhone) {
      setError('연락처를 입력해주세요');
      return false;
    }

    if (formData.adminPassword.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다');
      return false;
    }

    if (formData.adminPassword !== formData.adminPasswordConfirm) {
      setError('비밀번호가 일치하지 않습니다');
      return false;
    }

    if (!agreements.termsOfService) {
      setError('이용약관에 동의해주세요');
      return false;
    }

    if (!agreements.privacyPolicy) {
      setError('개인정보처리방침에 동의해주세요');
      return false;
    }

    return true;
  };

  // 2단계 검증
  const validateStep2 = () => {
    if (!region.province || !region.city) {
      setError('활동 지역을 모두 선택해주세요');
      return false;
    }

    if (!operationType) {
      setError('운영 체제를 선택해주세요');
      return false;
    }

    return true;
  };

  // 다음 단계
  const handleNext = () => {
    setError('');
    if (validateStep1()) {
      setStep(2);
    }
  };

  // 이전 단계
  const handlePrev = () => {
    setError('');
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateStep2()) {
      return;
    }

    setLoading(true);

    try {
      const res = await api.post('/api/auth/signup-academy', {
        academyCode: formData.academyCode,
        academyName: formData.academyName,
        adminEmail: formData.adminEmail,
        adminPassword: formData.adminPassword,
        adminName: formData.adminName,
        adminPhone: formData.adminPhone,
        region: {
          province: region.province,
          city: region.city,
          district: region.district || region.city,
          subDistrict: region.subDistrict || region.district || region.city
        },
        operationType: operationType
      });

      // 학원코드 저장 (자동 로그인용)
      localStorage.setItem('academyCode', res.data.tenantId);

      setSuccess({
        tenantId: res.data.tenantId,
        academyUrl: res.data.academyUrl,
        region: `${region.province} ${region.city}${region.district ? ' ' + region.district : ''}`,
        operationType: operationType === 'solo' ? '1인 체제' : '다인 체제'
      });

      setStep(3);

    } catch (err) {
      setError(err.response?.data?.error || '회원가입에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 가입 완료 화면
  if (step === 3 && success) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>EduStaff</h1>
            <p>가입이 완료되었습니다!</p>
          </div>

          <div className="success-message">
            <FiCheckCircle className="success-icon" size={64} />
            <h3>환영합니다!</h3>
            <p>{formData.academyName} 등록이 완료되었습니다.</p>

            <div className="academy-info">
              <div className="info-item">
                <label>학원코드</label>
                <code>{success.tenantId}</code>
              </div>
              <div className="info-item">
                <label>접속 주소</label>
                <code>{success.academyUrl}</code>
              </div>
              <div className="info-item">
                <label>활동 지역</label>
                <code>{success.region}</code>
              </div>
              <div className="info-item">
                <label>운영 체제</label>
                <code>{success.operationType}</code>
              </div>
            </div>

            {/* 운영 체제별 맞춤 플랜 추천 */}
            {(() => {
              const plan = getRecommendedPlan(operationType);
              return (
                <div className="plan-recommendation">
                  <div className="plan-header">
                    <FiStar size={24} />
                    <h4>추천 플랜</h4>
                  </div>
                  <p className="plan-message">{plan.message}</p>
                  <ul className="plan-features">
                    {plan.features.map((feature, index) => (
                      <li key={index}>
                        <FiCheck size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            <button
              className="login-btn"
              onClick={() => navigate('/login')}
            >
              로그인하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page register-page">
      <div className="login-container register-container">
        <div className="login-header">
          <h1>EduStaff</h1>
          <p>학원 관리 시스템 가입</p>
        </div>

        {/* 진행 단계 표시 */}
        <div className="register-steps">
          <div className={`step-indicator ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number">{step > 1 ? <FiCheck /> : '1'}</div>
            <div className="step-label">기본 정보</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">지역/운영</div>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* 1단계: 기본 정보 */}
        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="login-form register-form">
            <h4 className="section-title">학원 정보</h4>

            <div className="form-group">
              <label htmlFor="academyCode">
                학원코드 *
                <span className="label-hint">(영문/숫자, URL 경로로 사용)</span>
              </label>
              <div className="input-with-status">
                <input
                  id="academyCode"
                  name="academyCode"
                  type="text"
                  value={formData.academyCode}
                  onChange={handleChange}
                  placeholder="예: myacademy"
                  required
                  maxLength={20}
                />
                <div className="status-indicator">
                  {codeStatus.checking && <FiLoader className="spin" />}
                  {!codeStatus.checking && codeStatus.available === true && (
                    <FiCheck className="text-success" />
                  )}
                  {!codeStatus.checking && codeStatus.available === false && (
                    <FiX className="text-danger" />
                  )}
                </div>
              </div>
              {formData.academyCode && (
                <small className={codeStatus.available ? 'text-success' : 'text-danger'}>
                  {codeStatus.available
                    ? `edustaff.co.kr/${formData.academyCode} 사용 가능`
                    : codeStatus.reason}
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="academyName">학원명 *</label>
              <input
                id="academyName"
                name="academyName"
                type="text"
                value={formData.academyName}
                onChange={handleChange}
                placeholder="예: 좋은학원"
                required
              />
            </div>

            <h4 className="section-title">관리자 계정</h4>

            <div className="form-group">
              <label htmlFor="adminName">이름 *</label>
              <input
                id="adminName"
                name="adminName"
                type="text"
                value={formData.adminName}
                onChange={handleChange}
                placeholder="관리자 이름"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="adminEmail">이메일 *</label>
              <input
                id="adminEmail"
                name="adminEmail"
                type="email"
                value={formData.adminEmail}
                onChange={handleChange}
                placeholder="admin@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="adminPassword">비밀번호 *</label>
              <input
                id="adminPassword"
                name="adminPassword"
                type="password"
                value={formData.adminPassword}
                onChange={handleChange}
                placeholder="6자 이상"
                required
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label htmlFor="adminPasswordConfirm">비밀번호 확인 *</label>
              <input
                id="adminPasswordConfirm"
                name="adminPasswordConfirm"
                type="password"
                value={formData.adminPasswordConfirm}
                onChange={handleChange}
                placeholder="비밀번호 재입력"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="adminPhone">연락처 *</label>
              <input
                id="adminPhone"
                name="adminPhone"
                type="tel"
                value={formData.adminPhone}
                onChange={handleChange}
                placeholder="010-1234-5678"
                required
              />
            </div>

            <h4 className="section-title">약관 동의</h4>

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={agreements.termsOfService}
                  onChange={(e) => setAgreements({ ...agreements, termsOfService: e.target.checked })}
                />
                <span>
                  <a href="/terms" target="_blank" rel="noopener noreferrer">이용약관</a>에 동의합니다 (필수)
                </span>
              </label>
            </div>

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={agreements.privacyPolicy}
                  onChange={(e) => setAgreements({ ...agreements, privacyPolicy: e.target.checked })}
                />
                <span>
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">개인정보처리방침</a>에 동의합니다 (필수)
                </span>
              </label>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/login')}
              >
                취소
              </button>
              <button
                type="submit"
                className="login-btn"
                disabled={!codeStatus.available}
              >
                다음 단계
              </button>
            </div>
          </form>
        )}

        {/* 2단계: 지역 및 운영 체제 */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="login-form register-form">
            <div className="info-section">
              <FiMapPin />
              <h4>활동 지역</h4>
              <p>학원이 위치한 지역을 선택해주세요</p>
            </div>

            <div className="form-group">
              <label>시/도 *</label>
              <select
                value={region.province}
                onChange={(e) => setRegion({ province: e.target.value, city: '', district: '', subDistrict: '' })}
                required
              >
                <option value="">선택하세요</option>
                {Object.keys(REGIONS).map((prov) => (
                  <option key={prov} value={prov}>{prov}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>시/군/구 *</label>
              <select
                value={region.city}
                onChange={(e) => setRegion({ ...region, city: e.target.value, district: '', subDistrict: '' })}
                disabled={!region.province}
                required
              >
                <option value="">선택하세요</option>
                {cityOptions.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {hasDistricts && (
              <div className="form-group">
                <label>구 *</label>
                <select
                  value={region.district}
                  onChange={(e) => setRegion({ ...region, district: e.target.value, subDistrict: '' })}
                  disabled={!region.city}
                  required
                >
                  <option value="">선택하세요</option>
                  {districtOptions.map((district) => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>동/읍/면 <span className="label-hint">(선택)</span></label>
              <select
                value={region.subDistrict}
                onChange={(e) => setRegion({ ...region, subDistrict: e.target.value })}
                disabled={!region.city || (hasDistricts && !region.district)}
              >
                <option value="">선택안함</option>
                {subDistrictOptions.map((subDistrict) => (
                  <option key={subDistrict} value={subDistrict}>{subDistrict}</option>
                ))}
              </select>
            </div>

            <div className="info-section">
              <FiUsers />
              <h4>운영 체제</h4>
              <p>학원 운영 형태를 선택해주세요</p>
              <small style={{ color: '#6B7280', fontSize: '0.85rem' }}>
                * 운영 체제는 가입 후 설정에서 언제든지 변경하실 수 있습니다
              </small>
            </div>

            <div className="operation-type-selector">
              <div
                className={`operation-card ${operationType === 'solo' ? 'selected' : ''}`}
                onClick={() => setOperationType('solo')}
              >
                <FiUser size={32} />
                <h5>1인 체제</h5>
                <p>원장님 혼자 운영</p>
              </div>
              <div
                className={`operation-card ${operationType === 'team' ? 'selected' : ''}`}
                onClick={() => setOperationType('team')}
              >
                <FiUsers size={32} />
                <h5>다인 체제</h5>
                <p>원장님 + 강사진</p>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handlePrev}
              >
                이전
              </button>
              <button
                type="submit"
                className="login-btn"
                disabled={loading}
              >
                {loading ? '가입 중...' : '무료로 시작하기'}
              </button>
            </div>
          </form>
        )}

        <div className="login-footer">
          <p>이미 계정이 있으신가요? <Link to="/login">로그인</Link></p>
        </div>
      </div>
    </div>
  );
}
